import { action, computed, observable } from 'mobx'
import {
  type AllowedProgressionTypes,
  type AvailableProgressionOption,
  AvailableProgressions,
  type AvailableProgressionStep,
  type AllowedProgressionSteps,
} from '@/features/inventory/allowed-progressions'
import {
  type ExtensionBillingInfo,
  type ExtensionProgressionBilling,
} from '@/features/inventory/ExtensionBillingInfo'
import {
  ProgressionsNameMapping,
  ProgressionStepsNameMapping,
} from '@/features/inventory/inventory-item-display'
import { JobProgressionBillingModalViewModel } from './JobProgressionBillingModalViewModel'
import {
  type GetBillingType,
  RegularEditorViewModel,
} from './RegularEditorViewModel'

export interface SelectedProgression extends AvailableProgressionOption {
  steps: SelectedProgressionStep[]
}

export interface SelectedProgressionStep extends AvailableProgressionStep {
  pricingVm: RegularEditorViewModel
}

export type ProgressionMap = Map<AllowedProgressionTypes, SelectedProgression>

export type StepMap = Map<AllowedProgressionSteps, SelectedProgressionStep>

/**
 * Edits the per-step pricing of an extension's job progressions.
 */
export class ProgressionEditorViewModel {
  /**
   * Map of selected progressions
   */
  @observable accessor selectedProgressions: ProgressionMap = new Map()

  /**
   * Modal to handle adding a new progression
   */
  readonly modalVm = new JobProgressionBillingModalViewModel(
    () => this.availableProgressions,
    (progression, selected) => this.setProgression(progression, selected),
    () => this._getBillingType(),
  )

  constructor(private readonly _getBillingType: GetBillingType) {}

  /**
   * Provides the available non selected progressions
   */
  @computed
  get availableProgressions() {
    return AvailableProgressions.filter(
      (progression) => !this.selectedProgressions.has(progression.value),
    )
  }

  /**
   * Returns the selected progressions
   */
  @computed
  get selectedProgressionsList() {
    return Array.from(this.selectedProgressions.values())
  }

  /**
   * Whether a new progression can be selected
   */
  @computed
  get canAddProgression() {
    return this.selectedProgressions.size < AvailableProgressions.length
  }

  /**
   * Adds a new selected progression
   */
  @action.bound
  setProgression(
    progression: AllowedProgressionTypes,
    selected: SelectedProgression,
  ) {
    this.selectedProgressions.set(progression, selected)
  }

  /**
   * Removes the progression from the selected map
   */
  @action.bound
  removeProgression(progression: AllowedProgressionTypes) {
    this.selectedProgressions.delete(progression)
  }

  /**
   * Edits a selected progression
   */
  @action.bound
  editProgression(progression: SelectedProgression) {
    this.modalVm.show(progression)
  }

  /**
   * Produces the values needed to submit to the api
   */
  toValues(): ExtensionProgressionBilling {
    return {
      type: 'Progression',
      progressions: Array.from(this.selectedProgressions.values()).map(
        (progression) => ({
          progression: progression.value,
          steps: progression.steps.map((step) => {
            const { clientChargeAmount, proPayoutAmount } =
              step.pricingVm.toValues()

            return {
              step: step.value,
              clientChargeAmount,
              proPayoutAmount,
            }
          }),
        }),
      ),
    }
  }

  /**
   * Restores the UI from saved data
   */
  @action.bound
  hydrate(values: ExtensionBillingInfo): void {
    const { progressions } = values as ExtensionProgressionBilling

    progressions.forEach((progression) => {
      const steps = progression.steps.map((step) => {
        const pricingVm = new RegularEditorViewModel(this._getBillingType)

        pricingVm.hydrate({
          clientChargeAmount: step.clientChargeAmount,
          proPayoutAmount: step.proPayoutAmount,
          type: 'Regular',
        })

        return {
          name: ProgressionStepsNameMapping[step.step],
          value: step.step,
          pricingVm,
        }
      })

      this.selectedProgressions.set(progression.progression, {
        value: progression.progression,
        name: ProgressionsNameMapping[progression.progression],
        steps,
      })
    })
  }

  /**
   * Clears the selected progressions
   */
  @action.bound
  reset(): void {
    this.selectedProgressions.clear()
  }
}
